import IconCalendar from '@apollo/icons/default/IconCalendar.svg';
import IconClients from '@apollo/icons/default/IconClients.svg';
import IconSearch from '@apollo/icons/default/IconSearch.svg';
import IconChevronDown from '@apollo/icons/large/IconChevronDown.svg';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
} from '@apollo/orbit';
import { debounce, mapValues } from 'lodash';
import moment from 'moment';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router';

import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { PersistedQueriesFilterAndSortFragment } from 'src/app/graph/routes';
import {
  ClientFilterView,
  SelectedClients,
} from 'src/components/clientFilter/ClientFilter';
import { DateFilter } from 'src/components/dateFilter/DateFilter';
import { FilterPane } from 'src/components/metricsFilter/filterPane/FilterPane';
import { SplitButtonToggle } from 'src/components/splitButtonToggle/SplitButtonToggle';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { dateFilterOptionLabels } from 'src/lib/config/dateFilterOptions';
import { getAbsoluteDate } from 'src/lib/date/date';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import {
  RouteConfig,
  RouteConfigParams,
} from 'src/lib/routeConfig/RouteConfig';

import { PersistedQuerySortingControls } from '../persistedQuerySortingControls/PersistedQuerySortingControls';

type PersistedQueryFilterParams = RouteConfigParams<
  typeof PersistedQueriesFilterAndSortFragment
>;

function convertToRoutePatch(params: PersistedQueryFilterParams) {
  return mapValues(params, (value) =>
    value === undefined ? RouteConfig.DELETE : value,
  );
}

export const PersistedQueryFilters = ({
  clientNames,
  onChangeFilters,
}: {
  clientNames: string[];
  onChangeFilters: () => void;
}) => {
  const { currentPersistedQueriesListRoute } = useInternalGraphLinking();
  const params = useRouteParams(PersistedQueriesFilterAndSortFragment);

  const hasActiveFilters = Object.values(params).some(
    (value) => value !== undefined,
  );

  const history = useHistory();
  const location = useLocation();
  const setUrlFilter = useCallback(
    (filterParams: PersistedQueryFilterParams) => {
      history.push(
        currentPersistedQueriesListRoute.locationFrom({
          location,
          patch: convertToRoutePatch({
            ...params,
            ...filterParams,
          }),
        }),
      );
      onChangeFilters();
    },
    [
      currentPersistedQueriesListRoute,
      history,
      location,
      onChangeFilters,
      params,
    ],
  );

  const [nameSearch, setNameSearch] = useState(params.search || '');

  // Keep the search value in sync with the URL params
  const searchRef = useRef(nameSearch);
  searchRef.current = nameSearch;
  useEffect(() => {
    if (params.search !== searchRef.current) {
      setNameSearch(params.search ?? '');
    }
  }, [params.search]);

  const setNameSearchDebounced = React.useMemo(() => {
    const debouncedSetQueryParams = debounce(
      (update: string) => {
        setUrlFilter({
          search: update || undefined,
        });
      },
      500,
      { leading: false, trailing: true },
    );
    return (update: React.SetStateAction<string>) => {
      setNameSearch((currentVal) => {
        const newVal =
          typeof update === 'function' ? update(currentVal) : update;
        debouncedSetQueryParams(newVal);
        return newVal;
      });
    };
  }, [setUrlFilter]);

  // Note to use the clientFilter component expects versions but we don't have them here
  // so we pass `*` for all clients
  const allClients = useMemo(() => {
    return clientNames.reduce(
      (acc, clientName) => {
        if (!acc[clientName]) {
          // `*` is the wildcard for all clients
          acc[clientName] = ['*'];
        }
        return acc;
      },
      // `''` is the wildcard for all unidentified clients
      { '': ['*'] } as Record<string, Array<string>>,
    );
  }, [clientNames]);
  const selectedClients = useMemo(() => {
    if (params.clientNamesGroupSelection === 'all') {
      return undefined;
    } else if (params.clientNamesGroupSelection === 'none') {
      return {};
    }
    return params.clientNames?.reduce<SelectedClients>((acc, clientName) => {
      acc[clientName] = '*';
      return acc;
    }, {});
  }, [params.clientNamesGroupSelection, params.clientNames]);

  const totalSelectedClients = Object.keys(selectedClients ?? {}).length;

  const onResetFilters = useCallback(() => {
    setUrlFilter({
      search: undefined,
      lastPublishedType: undefined,
      lastPublishedDate: undefined,
      lastPublishedOption: undefined,
      clientNames: undefined,
      clientNamesGroupSelection: undefined,
    });
  }, [setUrlFilter]);

  const hasActiveLastPublishedFilters =
    params.lastPublishedType !== undefined ||
    params.lastPublishedDate !== undefined ||
    params.lastPublishedOption !== undefined;
  const selectedLastPublishedType = params.lastPublishedType ?? 'before';
  const selectedLastPublishedOption = params.lastPublishedOption ?? 'now';
  const lastPublishedDate = useMemo(
    () =>
      getAbsoluteDate(
        selectedLastPublishedOption,
        params.lastPublishedDate
          ? new Date(params.lastPublishedDate)
          : undefined,
      ),
    [params.lastPublishedDate, selectedLastPublishedOption],
  );

  return (
    <div className="flex pb-2 pt-3 max-lg:flex-col max-lg:space-y-3 lg:space-x-2">
      <FormControl>
        <FormLabel className="sr-only">Search</FormLabel>
        <Input
          spellCheck={false}
          value={nameSearch}
          placeholder="Search"
          onChange={(e) => setNameSearchDebounced(e.target.value)}
          leftElement={<IconSearch className="size-4" />}
        />
      </FormControl>
      <div className="flex space-x-2">
        <PersistedQuerySortingControls
          className="lg:hidden"
          variant="secondary"
          sortColumn={
            params.sortColumn ?? GraphQLTypes.PersistedQuerySortColumn.NAME
          }
          sortDirection={
            params.sortDirection ?? GraphQLTypes.Ordering.ASCENDING
          }
        />
        <Popover placement="bottom-end" isLazy>
          <PopoverTrigger>
            <div>
              <Button
                variant="secondary"
                leftIcon={<IconCalendar />}
                rightIcon={<IconChevronDown />}
              >
                {hasActiveLastPublishedFilters ? (
                  <span className="capitalize">
                    {selectedLastPublishedType}{' '}
                    {selectedLastPublishedOption === 'custom'
                      ? moment(lastPublishedDate)
                          .add(1, 'day')
                          .format('MMM D, YYYY')
                      : dateFilterOptionLabels[selectedLastPublishedOption]}
                  </span>
                ) : (
                  'Publish Date'
                )}
              </Button>
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-60">
            <FilterPane title="Publish Date">
              <SplitButtonToggle
                className="mb-2 w-full"
                items={['before', 'after']}
                selectedItem={selectedLastPublishedType}
                onToggle={(newItem) =>
                  setUrlFilter({
                    lastPublishedType: newItem,
                  })
                }
              />
              <DateFilter
                className="space-y-2"
                date={lastPublishedDate}
                option={selectedLastPublishedOption}
                options={[
                  'now',
                  'today',
                  'thisWeek',
                  'thisMonth',
                  'lastThreeMonths',
                  'custom',
                ]}
                onDateSelected={(option, date) =>
                  setUrlFilter({
                    lastPublishedDate: date,
                    lastPublishedOption: option,
                  })
                }
              />
            </FilterPane>
          </PopoverContent>
        </Popover>
        {clientNames.length > 0 && (
          <Popover placement="bottom-end" isLazy>
            <PopoverTrigger>
              <Button
                variant="secondary"
                leftIcon={<IconClients />}
                rightIcon={<IconChevronDown />}
              >
                {selectedClients && totalSelectedClients > 0
                  ? `${totalSelectedClients} ${format.pluralize(totalSelectedClients, 'Client')}`
                  : params.clientNamesGroupSelection === 'all' ||
                      !params.clientNamesGroupSelection
                    ? 'All Clients'
                    : 'No Clients'}
              </Button>
            </PopoverTrigger>
            <div className="z-50">
              <PopoverContent>
                <PopoverBody className="max-h-[calc(100vh-256px)] overflow-y-auto p-0 [&_h3]:mb-2 [&_h3]:text-sm">
                  <ClientFilterView
                    allClients={allClients}
                    selectedClients={selectedClients}
                    setSelectedClients={(newSelectedClients) => {
                      if (newSelectedClients === null) {
                        setUrlFilter({
                          clientNames: undefined,
                          clientNamesGroupSelection: 'all',
                        });
                      } else if (Object.keys(newSelectedClients).length === 0) {
                        setUrlFilter({
                          clientNames: undefined,
                          clientNamesGroupSelection: 'none',
                        });
                      } else {
                        const selectedClientNames =
                          Object.keys(newSelectedClients);
                        setUrlFilter({
                          clientNames: selectedClientNames,
                          clientNamesGroupSelection: undefined,
                        });
                      }
                    }}
                    canViewVersions={false}
                    clientThresholdToShowSearch={10}
                    title="Clients"
                  />
                </PopoverBody>
              </PopoverContent>
            </div>
          </Popover>
        )}
        <Button
          variant="primary"
          isDisabled={!hasActiveFilters}
          onClick={onResetFilters}
        >
          Reset
        </Button>
      </div>
    </div>
  );
};
